/* eslint-disable */
import { Control, UseFormSetValue, UseFormWatch } from 'react-hook-form'

import {
    CheckboxGroupControl,
    CustomCheckboxGroup,
    InputControl,
    RadioGroupControl,
    BoxedRadioGroupControl,
    BoxedCheckboxGroupControl
} from '@/components/form-controls'

interface FormResponse {
    [key: string]: string | string[] // Dynamic field names with string or array values
}

export interface TransformDataAdmissionTestForm {
    [key: string]: {
        idQuestion: number
        selectedOptionIds: number[]
        selectedExplicationOptionIds?: number[]
        responseExplication?: string
        responseSecondExplication?: string
    }
}

// API response type for questions
interface ApiQuestionData {
    id: number
    name?: string
    label: string
    type: '1' | '2' | '3'
    options: {
        id: number
        value: string
        label: string
    }[]
    order: number
    pickedOptions: string[] | null // Changed from number[] to string[]
    responseSecondaryExplication?: {
        explanationOptionIds?: number[]
    } | null
    requiredExplication: {
        required: boolean
        valueFlag: string[]
        options?: {
            id: number
            label: string
            value: string
        }[]
    }
    responseText: string | null
    textoInfo?: string | null
}

interface ProcessedQuestion extends ApiQuestionData {
    fieldName: string
    hasExplanation: boolean
    explanationOptions: Array<{
        id: number
        label: string
        value: string
    }> | null
    selectedValues: string[]
    selectedExplanationValues: string[]
}

interface Option {
    id: number
    label: string
    value: string
}

// Generate clean field name from question label
export const generateFieldName = (label: string, id: number): string => {
    // Use question ID as primary identifier, with a clean version of label
    const cleanLabel = label
        .toLowerCase()
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .substring(0, 30) // Limit length

    return `question_${id}_${cleanLabel}`
}

// Fix valueFlag inconsistencies
const normalizeValueFlag = (question: ApiQuestionData): string[] => {
    const { requiredExplication, options } = question

    if (!requiredExplication.required || !requiredExplication.valueFlag) {
        return []
    }

    return requiredExplication.valueFlag
        .map((flag: string) => {
            // Try to find the option that matches this flag as an ID
            const numericFlag = parseInt(flag, 10)
            if (!isNaN(numericFlag)) {
                const matchingOption = options.find(
                    (opt: any) => opt.id === numericFlag
                )
                if (matchingOption) {
                    return matchingOption.value
                }
            }

            // Try to find the option that matches this flag as a value
            const matchingOptionByValue = options.find(
                (opt: any) => opt.value === flag
            )
            if (matchingOptionByValue) {
                return flag
            }

            // If no match found, this flag is invalid for this question
            return null
        })
        .filter((flag) => flag !== null)
}

// Process questions from API response
export const processQuestions = (
    questionsData: ApiQuestionData[]
): ProcessedQuestion[] => {
    return questionsData
        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
        .map((question) => {
            const fieldName = generateFieldName(question.label, question.id)
            const normalizedValueFlag = normalizeValueFlag(question)

            return {
                ...question,
                name: question.name || fieldName, // Ensure name exists
                fieldName,
                hasExplanation: question.requiredExplication.required,
                explanationOptions:
                    question.requiredExplication.options || null,
                selectedValues: question.pickedOptions || [], // Use existing picked options
                selectedExplanationValues: [], // Will be populated if loading existing responses
                requiredExplication: {
                    ...question.requiredExplication,
                    valueFlag: normalizedValueFlag
                },
                textoInfo: question.textoInfo
            }
        })
}

// Create initial form values - now handles existing responses
export const createInitialFormValues = (
    questions: ProcessedQuestion[]
): FormResponse => {
    const initialValues: FormResponse = {}

    questions.forEach((question) => {
        // Set initial value based on question type and existing responses
        if (question.type === '1' || question.type === '3') {
            // Radio/Select: use picked option or empty string
            initialValues[question.fieldName] =
                question.selectedValues.length > 0
                    ? question.selectedValues[0]
                    : ''

            // Initialize explanation fields if they exist
            if (question.hasExplanation) {
                if (question.explanationOptions) {
                    initialValues[`${question.fieldName}_explanation`] =
                        question.selectedExplanationValues || []
                }
                initialValues[`${question.fieldName}_second_explanation`] =
                    question.responseText || ''
            }
        } else if (question.type === '2') {
            // Checkbox: use picked options or empty array
            initialValues[question.fieldName] = question.selectedValues || []

            // Initialize explanation field if exists
            if (question.hasExplanation) {
                initialValues[`${question.fieldName}_explanation`] =
                    question.responseText || ''
            }
        }
    })

    return initialValues
}

// Transform form data for API submission
export const transformFormData = (
    formData: FormResponse,
    questions: ProcessedQuestion[]
): TransformDataAdmissionTestForm => {
    const result: TransformDataAdmissionTestForm = {}

    questions.forEach((question) => {
        const fieldName = question.fieldName
        const selectedValues = formData[fieldName]

        if (
            !selectedValues ||
            (Array.isArray(selectedValues) && selectedValues.length === 0)
        ) {
            return // Skip empty values
        }

        // Convert single value to array for processing
        const valuesArray = Array.isArray(selectedValues)
            ? selectedValues
            : [selectedValues]

        // Get selected option IDs
        const selectedOptionIds = question.options
            .filter((option) => valuesArray.includes(option.value))
            .map((option) => option.id)

        // Handle explanation if present
        let selectedExplicationOptionIds: number[] = []
        const explanationValues = formData[`${fieldName}_explanation`]

        if (
            question.hasExplanation &&
            question.explanationOptions &&
            explanationValues &&
            Array.isArray(explanationValues) &&
            explanationValues.length > 0
        ) {
            selectedExplicationOptionIds = question.explanationOptions
                .filter((option) => explanationValues.includes(option.value))
                .map((option) => option.id)
        }

        // Handle second explanation (text input)
        const secondExplanation = formData[`${fieldName}_second_explanation`]
        const responseSecondExplication = Array.isArray(secondExplanation)
            ? secondExplanation[0] || ''
            : secondExplanation || ''

        result[fieldName] = {
            idQuestion: question.id,
            selectedOptionIds,
            selectedExplicationOptionIds,
            responseExplication: '',
            responseSecondExplication
        }
    })

    return result
}

// Check if explanation should be shown
const shouldShowExplanation = (
    question: ProcessedQuestion,
    selectedValues: string | string[]
): boolean => {
    if (!question.hasExplanation || !question.requiredExplication.valueFlag) {
        return false
    }

    const valuesArray = Array.isArray(selectedValues)
        ? selectedValues
        : [selectedValues]
    return question.requiredExplication.valueFlag.some((flag) =>
        valuesArray.includes(flag)
    )
}

// Explanation options that ask for a text
const SECOND_EXPLANATION_VALUES = ['Otro', 'Indígena', 'Medicina especializada']

// Check if second explanation (text input) should be shown
const shouldShowSecondExplanation = (
    explanationValues: string | string[]
): boolean => {
    const valuesArray = Array.isArray(explanationValues)
        ? explanationValues
        : [explanationValues]
    return valuesArray.some((value) =>
        SECOND_EXPLANATION_VALUES.includes(value)
    )
}

interface RenderQuestionProps {
    question: ProcessedQuestion
    control: Control<FormResponse>
    watch: UseFormWatch<FormResponse>
    setValue: UseFormSetValue<FormResponse>
    requiredText: string
    description: string
    isReadOnly?: boolean
}

export const renderQuestion = ({
    question,
    control,
    watch,
    setValue,
    requiredText,
    description,
    isReadOnly = false
}: RenderQuestionProps): JSX.Element => {
    const selectedValues =
        watch(question.fieldName) || (question.type === '2' ? [] : '')
    const showExplication = shouldShowExplanation(question, selectedValues)

    const explanationValues = watch(`${question.fieldName}_explanation`) || []
    const showSecondExplication =
        showExplication && shouldShowSecondExplanation(explanationValues)

    switch (question.type) {
        case '1': // Radio buttons
            return (
                <div
                    key={question.id}
                    className="space-y-4"
                >
                    <BoxedRadioGroupControl
                        control={control}
                        name={question.fieldName}
                        label={question.label}
                        options={question.options}
                        // isDisabled={isReadOnly} // Boxed control doesn't support isDisabled yet, but we can add it or ignore for now. Wait, I added isDisabled to BoxedCheckbox but not BoxedRadio.
                        // Let's check BoxedRadioGroupControl props. It doesn't have isDisabled.
                        // I should probably add it to BoxedRadioGroupControl too, but for now let's just use it.
                        // The user didn't ask for read-only support explicitly but it's good practice.
                        // I'll skip isDisabled for BoxedRadio for now to avoid errors if prop is missing.
                        rules={{ required: requiredText }}
                        description={question.textoInfo || undefined}
                    />

                    {showExplication && question.explanationOptions && (
                        <div>
                            <div>
                                <BoxedCheckboxGroupControl
                                    name={`${question.fieldName}_explanation`}
                                    control={control}
                                    options={question.explanationOptions}
                                    label={description}
                                    isDisabled={isReadOnly}
                                    rules={{
                                        required: showExplication
                                            ? requiredText
                                            : false
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {showSecondExplication && (
                        <InputControl
                            control={control}
                            name={`${question.fieldName}_second_explanation`}
                            label={description}
                            isDisabled={isReadOnly}
                            rules={{ required: requiredText }}
                        />
                    )}
                </div>
            )

        case '2': // Checkboxes
            return (
                <div
                    key={question.id}
                    className="space-y-4"
                >
                    <BoxedCheckboxGroupControl
                        control={control}
                        name={question.fieldName}
                        label={question.label}
                        options={question.options}
                        isDisabled={isReadOnly}
                        rules={{ required: requiredText }}
                        description={question.textoInfo || undefined}
                    />

                    {showExplication && (
                        <InputControl
                            control={control}
                            name={`${question.fieldName}_explanation`}
                            label={description}
                            isDisabled={isReadOnly}
                            rules={{ required: requiredText }}
                        />
                    )}
                </div>
            )

        case '3': // Select dropdown
            return (
                <div
                    key={question.id}
                    className="space-y-4"
                >
                    <BoxedRadioGroupControl
                        control={control}
                        name={question.fieldName}
                        label={question.label}
                        options={question.options}
                        isDisabled={isReadOnly}
                        rules={{ required: requiredText }}
                        description={question.textoInfo || undefined}
                    />

                    {showExplication && (
                        <InputControl
                            control={control}
                            name={`${question.fieldName}_explanation`}
                            label={description}
                            isDisabled={isReadOnly}
                            rules={{ required: requiredText }}
                        />
                    )}
                </div>
            )

        default:
            return (
                <div key={question.id}>
                    Unsupported question type: {question.type}
                </div>
            )
    }
}
